import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

const API_BASE = process.env.REACT_APP_API_BASE || '';

export type VisitPeriod = 'MORNING' | 'MIDDAY' | 'EVENING';

export interface Visit {
  id: string;
  parkName: string;
  visitDate: string;
  selectedAttractionCount: number;
  tripId: string | null;
  visitPeriod: VisitPeriod;
}

export function useVisits(page = 0, tripId?: string) {
  return useQuery({
    queryKey: ['visits', page, tripId],
    queryFn: async () => {
      let url = `${API_BASE}/api/visits?page=${page}&size=50`;
      if (tripId) url += `&tripId=${tripId}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Server error: ${response.status}`);
      return response.json() as Promise<Visit[]>;
    },
  });
}

/** Fetch all visits for a specific trip (unpaginated) */
export function useTripsVisits(tripId: string) {
  return useQuery({
    queryKey: ['trips-visits', tripId],
    queryFn: async () => {
      const response = await fetch(`${API_BASE}/api/visits?tripId=${tripId}&page=0&size=1000`);
      if (!response.ok) throw new Error(`Server error: ${response.status}`);
      return response.json() as Promise<Visit[]>;
    },
    enabled: !!tripId,
  });
}

export function useCreateVisit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { parkName: string; visitDate: string; tripId?: string; newTripName?: string; newTripStartDate?: string; newTripEndDate?: string; visitPeriod?: VisitPeriod }) => {
      const response = await fetch(`${API_BASE}/api/visits`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to create visit');
      }
      return response.json() as Promise<Visit>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['visits'] });
      queryClient.invalidateQueries({ queryKey: ['trips-visits'] });
      queryClient.invalidateQueries({ queryKey: ['trips'] });
    },
  });
}

export function useDeleteVisit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (visitId: string) => {
      const response = await fetch(`${API_BASE}/api/visits/${visitId}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Failed to delete visit');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['visits'] });
      queryClient.invalidateQueries({ queryKey: ['trips-visits'] });
      queryClient.invalidateQueries({ queryKey: ['trips'] });
    },
  });
}
